/**
 * Convert common LaTeX shorthand into Unicode superscripts / subscripts /
 * symbols. Used for contexts where full KaTeX rendering isn't practical
 * (mind-map node labels, diagram captions, tags).
 *
 * Handles:
 *   \times \cdot \pm \Delta \pi \mu \alpha \beta \gamma etc. → symbols
 *   10^{23}  cm^{3}  x^2   → superscript digits
 *   H_{2}O   K_{a}   v_f   → subscript digits/letters
 *   ->  →  \rightarrow      → →
 *   Also strips leftover `\` used as escape spam.
 */

const SUP_MAP = {
  "0":"⁰","1":"¹","2":"²","3":"³","4":"⁴","5":"⁵","6":"⁶","7":"⁷","8":"⁸","9":"⁹",
  "+":"⁺","-":"⁻","=":"⁼","(":"⁽",")":"⁾",
  "a":"ᵃ","b":"ᵇ","c":"ᶜ","d":"ᵈ","e":"ᵉ","f":"ᶠ","g":"ᵍ","h":"ʰ",
  "i":"ⁱ","j":"ʲ","k":"ᵏ","l":"ˡ","m":"ᵐ","n":"ⁿ","o":"ᵒ","p":"ᵖ",
  "r":"ʳ","s":"ˢ","t":"ᵗ","u":"ᵘ","v":"ᵛ","w":"ʷ","x":"ˣ","y":"ʸ","z":"ᶻ",
};
const SUB_MAP = {
  "0":"₀","1":"₁","2":"₂","3":"₃","4":"₄","5":"₅","6":"₆","7":"₇","8":"₈","9":"₉",
  "+":"₊","-":"₋","=":"₌","(":"₍",")":"₎",
  "a":"ₐ","e":"ₑ","h":"ₕ","i":"ᵢ","j":"ⱼ","k":"ₖ","l":"ₗ","m":"ₘ","n":"ₙ",
  "o":"ₒ","p":"ₚ","r":"ᵣ","s":"ₛ","t":"ₜ","u":"ᵤ","v":"ᵥ","x":"ₓ",
};

const CMD_MAP = {
  "\\times": "×", "\\cdot": "·", "\\pm": "±", "\\mp": "∓",
  "\\rightarrow": "→", "\\leftarrow": "←", "\\Rightarrow": "⇒", "\\Leftarrow": "⇐",
  "\\to": "→",
  "\\leq": "≤", "\\geq": "≥", "\\neq": "≠", "\\approx": "≈", "\\equiv": "≡",
  "\\infty": "∞", "\\partial": "∂", "\\nabla": "∇",
  "\\Delta": "Δ", "\\delta": "δ", "\\Sigma": "Σ", "\\sigma": "σ",
  "\\alpha": "α", "\\beta": "β", "\\gamma": "γ", "\\Gamma": "Γ",
  "\\theta": "θ", "\\Theta": "Θ", "\\lambda": "λ", "\\Lambda": "Λ",
  "\\mu": "µ", "\\nu": "ν", "\\pi": "π", "\\Pi": "Π", "\\rho": "ρ",
  "\\phi": "φ", "\\Phi": "Φ", "\\omega": "ω", "\\Omega": "Ω",
  "\\epsilon": "ε", "\\varepsilon": "ε", "\\tau": "τ", "\\chi": "χ",
  "\\ell": "ℓ", "\\hbar": "ℏ",
  "\\degree": "°",
};

function mapEach(chunk, table) {
  let out = "";
  for (const ch of chunk) out += table[ch] || ch;
  return out;
}

export function latexToUnicode(input) {
  if (input == null) return "";
  let s = String(input);

  // 1) Named commands (longest-match first, backslash-terminated by word boundary)
  s = s.replace(/\\[A-Za-z]+/g, (m) => CMD_MAP[m] || m);

  // 2) `^{...}` and `_{...}` → unicode super/sub (only if all chars mappable)
  s = s.replace(/\^\{([^{}]{1,10})\}/g, (m, body) => {
    const mapped = mapEach(body, SUP_MAP);
    return [...body].every((c) => SUP_MAP[c]) ? mapped : `^${body}`;
  });
  s = s.replace(/_\{([^{}]{1,10})\}/g, (m, body) => {
    const mapped = mapEach(body, SUB_MAP);
    return [...body].every((c) => SUB_MAP[c]) ? mapped : `_${body}`;
  });

  // 3) Bare `x^23` (no braces)
  s = s.replace(/\^([+\-]?[0-9]+)/g, (_, d) => mapEach(d, SUP_MAP));
  s = s.replace(/\^([A-Za-z])(?![A-Za-z])/g, (_, c) =>
    SUP_MAP[c] ? SUP_MAP[c] : `^${c}`
  );

  // 4) Bare `x_2` (no braces)
  s = s.replace(/_([0-9]+)/g, (_, d) => mapEach(d, SUB_MAP));

  // 5) Chemical-formula subscripts: `H2O`, `CO2`, `C6H12O6` — but ONLY when the
  //    preceding char is a capital letter or a closing `)`.
  s = s.replace(/([A-Z][a-z]?|\))([0-9]+)/g, (_, sym, d) => sym + mapEach(d, SUB_MAP));

  // 6) Reaction arrow shorthand
  s = s.replace(/->/g, "→");

  // 7) Clean up any remaining single-backslashes that are followed by punctuation
  //    or nothing meaningful (e.g. `\ ` leftover), but keep `\\` literal safety.
  return s;
}

/**
 * Aggressively sanitise Mermaid diagram code emitted by an LLM. Common failure
 * modes:
 *   - Literal `\n` between lines instead of real newlines (Mermaid reports
 *     "Parse error on line 1: flowchart TD\nA[Matter] --> B[Mi …")
 *   - Fenced code (`\`\`\`mermaid` wrapper)
 *   - Trailing/leading whitespace or `mermaid` header word
 * Returns the cleaned code (or "" if unsalvageable).
 */
export function sanitiseMermaid(code) {
  if (!code) return "";
  let s = String(code).trim();
  // Strip ``` fences
  s = s.replace(/^```(?:mermaid)?\s*/i, "").replace(/\s*```$/i, "");
  // Convert literal `\n` sequences into real newlines
  s = s.replace(/\\n/g, "\n").replace(/\\r/g, "");
  // Drop a leading `mermaid` header word if present
  s = s.replace(/^\s*mermaid\s*\n?/i, "");
  return s.trim();
}

/**
 * Return true if a Mermaid snippet parses cleanly with the passed `mermaid`
 * library instance. Uses the async `.parse()` API which is much cheaper than
 * `.render()`.
 */
export async function isValidMermaid(mermaid, code) {
  if (!code) return false;
  try {
    await mermaid.parse(code);
    return true;
  } catch {
    return false;
  }
}
